import apiClient from './apiClient';
import {
  CommunityComment,
  CommunityDirectMessage,
  CommunityDirectMessageThread,
  CommunityMediaUploadResponse,
  CommunityPost,
  CommunityPostCreatePayload,
  CommunityPostListResponse,
  CommunityPostUpdatePayload,
  CommunityTopic,
  CommunityTrendingTopic,
} from '../types';

export interface CommunityFeedParams {
  limit?: number;
  cursor?: string | null;
  topic?: string | null;
  author_id?: number | null;
  search?: string | null;
  followed_only?: boolean;
  sort?: 'latest' | 'trending';
}

export interface CommunityCommentPayload {
  content: string;
  parent_comment_id?: string | null;
}

export async function fetchCommunityTopics(): Promise<CommunityTopic[]> {
  const response = await apiClient.get<{ items: CommunityTopic[] }>('/community/topics');
  return response.data.items;
}

export async function uploadCommunityMedia(file: File): Promise<CommunityMediaUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<CommunityMediaUploadResponse>('/community/media', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

export async function fetchCommunityPosts(params?: CommunityFeedParams): Promise<CommunityPostListResponse> {
  const response = await apiClient.get<CommunityPostListResponse>('/community/posts', {
    params,
  });
  return response.data;
}

export async function fetchCommunityPost(postId: string): Promise<CommunityPost> {
  const response = await apiClient.get<CommunityPost>(`/community/posts/${postId}`);
  return response.data;
}

export async function fetchTrendingTopics(limit?: number): Promise<CommunityTrendingTopic[]> {
  const response = await apiClient.get<{ items: CommunityTrendingTopic[] }>('/community/trending/topics', {
    params: { limit },
  });
  return response.data.items;
}

export async function createCommunityPost(payload: CommunityPostCreatePayload): Promise<CommunityPost> {
  const response = await apiClient.post<CommunityPost>('/community/posts', payload);
  return response.data;
}

export async function updateCommunityPost(postId: string, payload: CommunityPostUpdatePayload): Promise<CommunityPost> {
  const response = await apiClient.patch<CommunityPost>(`/community/posts/${postId}`, payload);
  return response.data;
}

export async function deleteCommunityPost(postId: string): Promise<void> {
  await apiClient.delete(`/community/posts/${postId}`);
}

export async function likeCommunityPost(postId: string): Promise<CommunityPost> {
  const response = await apiClient.post<CommunityPost>(`/community/posts/${postId}/like`);
  return response.data;
}

export async function unlikeCommunityPost(postId: string): Promise<CommunityPost> {
  const response = await apiClient.delete<CommunityPost>(`/community/posts/${postId}/like`);
  return response.data;
}

export async function followAuthor(authorId: number): Promise<boolean> {
  const response = await apiClient.post<{ following: boolean }>(`/community/users/${authorId}/follow`);
  return response.data.following;
}

export async function unfollowAuthor(authorId: number): Promise<boolean> {
  const response = await apiClient.delete<{ following: boolean }>(`/community/users/${authorId}/follow`);
  return response.data.following;
}

export async function fetchCommunityComments(postId: string, params?: { limit?: number; offset?: number }): Promise<CommunityComment[]> {
  const response = await apiClient.get<CommunityComment[]>(`/community/posts/${postId}/comments`, {
    params,
  });
  return response.data;
}

export async function createCommunityComment(postId: string, payload: CommunityCommentPayload): Promise<CommunityComment> {
  const response = await apiClient.post<CommunityComment>(`/community/posts/${postId}/comments`, payload);
  return response.data;
}

export async function deleteCommunityComment(postId: string, commentId: string): Promise<{ removed: number }> {
  const response = await apiClient.delete<{ deleted: boolean; comment_id: string; removed?: number }>(
    `/community/posts/${postId}/comments/${commentId}`,
  );
  return { removed: response.data.removed ?? 1 };
}

export async function likeCommunityComment(postId: string, commentId: string): Promise<CommunityComment> {
  const response = await apiClient.post<CommunityComment>(
    `/community/posts/${postId}/comments/${commentId}/like`,
  );
  return response.data;
}

export async function unlikeCommunityComment(postId: string, commentId: string): Promise<CommunityComment> {
  const response = await apiClient.delete<CommunityComment>(
    `/community/posts/${postId}/comments/${commentId}/like`,
  );
  return response.data;
}

export async function ensureDirectMessageThread(userId: number): Promise<CommunityDirectMessageThread> {
  const response = await apiClient.post<CommunityDirectMessageThread>(`/community/users/${userId}/dm`);
  return response.data;
}

export async function fetchDirectMessageThreads(): Promise<CommunityDirectMessageThread[]> {
  const response = await apiClient.get<CommunityDirectMessageThread[]>(`/community/dm/threads`);
  return response.data;
}

export async function fetchDirectMessages(
  threadId: string,
  params?: { limit?: number },
): Promise<CommunityDirectMessage[]> {
  const response = await apiClient.get<CommunityDirectMessage[]>(
    `/community/dm/threads/${threadId}/messages`,
    { params },
  );
  return response.data;
}

export async function sendDirectMessage(
  threadId: string,
  content: string,
): Promise<CommunityDirectMessage> {
  const response = await apiClient.post<CommunityDirectMessage>(
    `/community/dm/threads/${threadId}/messages`,
    { content },
  );
  return response.data;
}
